import React, { useState } from 'react';

import { useEntitlements, subscribeTier, cancelSubscription, fetchEntitlements } from '../../services/entitlementsService';

const ACCENT = '#F15A29';

// Self-serve plans shown to the signed-in customer. Prices mirror the
// api-shipments TIER_PRICING (free $0, pro $99, enterprise $299).
export const PLANS = [
  {
    tier: 'free',
    label: 'Free',
    price: 0,
    blurb: 'Core booking & live tracking',
    features: ['Book deliveries', 'Live tracking', 'Driver chat', 'Proof of delivery', 'Digital BOL']
  },
  {
    tier: 'pro',
    label: 'Pro',
    price: 99,
    blurb: 'All premium features unlocked',
    features: ['Everything in Free', 'Bill \u2014 AI assistant', 'Metrics dashboard', 'Multi-drop routing', 'FedEx rate options', 'Pay by bank (ACH)']
  },
  {
    tier: 'enterprise',
    label: 'Enterprise',
    price: 299,
    blurb: 'Multi-location + dedicated manager',
    features: ['Everything in Pro', 'Multi-location management', 'Dedicated account manager']
  }
];

const TIER_RANK = { free: 0, founding: 1, pro: 2, enterprise: 3 };
export const formatPlanPrice = price => (price > 0 ? '$' + price + '/mo' : '$0');
export const planTierLabel = tier => {
  const meta = PLANS.find(p => p.tier === tier);
  if (meta) return meta.label;
  return tier === 'founding' ? 'Founding' : tier;
};

// Reusable self-serve plan panel: current plan + upgrade/downgrade cards.
const PlanSelector = () => {
  const ent = useEntitlements();
  const [working, setWorking] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  if (!ent) {
    return <div style={{ padding: 16, color: '#777' }}>Loading your plan\u2026</div>;
  }

  const tier = ent.tier || 'free';
  const currentRank = TIER_RANK[tier] != null ? TIER_RANK[tier] : 0;
  const currentPrice = (PLANS.find(p => p.tier === tier) || {}).price || 0;
  const isFounding = tier === 'founding';

  const choose = targetTier => {
    if (working || targetTier === tier) return;
    setWorking(targetTier);
    setError('');
    setNotice('');
    const action = targetTier === 'free' ? cancelSubscription() : subscribeTier(targetTier);
    action.then(result => {
      setWorking('');
      if (!result || !result.ok) {
        setError((result && result.message) || 'Could not change your plan. Please try again.');
        return;
      }
      setNotice('Plan changed to ' + planTierLabel(targetTier) + '.');
      fetchEntitlements(true);
    });
  };

  return (
    <div>
      <div style={{ fontSize: 15, marginBottom: 14 }}>
        Current plan:{' '}
        <strong style={{ color: ACCENT }}>{planTierLabel(tier)}</strong>{' '}
        <span style={{ opacity: 0.7 }}>({formatPlanPrice(currentPrice)})</span>
        {ent.trial_active ? <span style={{ marginLeft: 8, color: ACCENT }}>Trial active</span> : null}
      </div>

      {isFounding ? (
        <div style={{ marginBottom: 14, fontSize: 13, color: '#555' }}>
          You&apos;re on a grandfathered <strong>Founding</strong> plan &mdash; Pro features at $0. You can move to Enterprise below; downgrading to Free gives up the Pro features.
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, maxWidth: 820 }}>
        {PLANS.map(plan => {
          const isCurrent = plan.tier === tier;
          const rank = TIER_RANK[plan.tier];
          const direction = rank > currentRank ? 'Upgrade' : 'Downgrade';
          const busy = working === plan.tier;
          return (
            <div
              key={plan.tier}
              style={{
                border: isCurrent ? '2px solid ' + ACCENT : '1px solid rgba(0,0,0,.12)',
                borderRadius: 12,
                padding: 18,
                display: 'flex',
                flexDirection: 'column',
                background: '#fff'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 700, fontSize: 17, color: '#1a1a1a' }}>{plan.label}</span>
                {isCurrent ? <span style={{ fontSize: 11, color: ACCENT, fontWeight: 700 }}>CURRENT</span> : null}
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, margin: '4px 0 2px', color: '#1a1a1a' }}>{formatPlanPrice(plan.price)}</div>
              <div style={{ fontSize: 12, opacity: 0.7, minHeight: 32, color: '#444' }}>{plan.blurb}</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 14px', fontSize: 13, flex: '1 1 auto' }}>
                {plan.features.map(label => (
                  <li key={label} style={{ padding: '3px 0', color: '#333' }}>
                    <span style={{ color: '#4caf50', marginRight: 6 }}>&#10003;</span>
                    {label}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled={isCurrent || busy}
                onClick={() => choose(plan.tier)}
                style={{
                  width: '100%',
                  borderRadius: 22,
                  padding: '11px 14px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: isCurrent || busy ? 'default' : 'pointer',
                  border: isCurrent ? '1px solid rgba(0,0,0,.2)' : 'none',
                  background: isCurrent ? 'transparent' : ACCENT,
                  color: isCurrent ? '#777' : '#fff',
                  opacity: busy ? 0.7 : 1
                }}
              >
                {isCurrent ? 'Current plan' : busy ? 'Working\u2026' : direction + ' to ' + plan.label}
              </button>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 14, fontSize: 12, color: '#777' }}>
        Paid plans are billed monthly to your card or bank on file. Cancel (downgrade to Free) anytime.
      </div>
      {notice ? <div style={{ marginTop: 10, fontSize: 13, color: '#2e7d32' }}>{notice}</div> : null}
      {error ? <div style={{ marginTop: 10, fontSize: 13, color: '#c0392b' }}>{error}</div> : null}
    </div>
  );
};

export default PlanSelector;
